import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldCheck, Loader2, CheckCircle2, AlertCircle, Building2, ArrowRight } from 'lucide-react';

const ERR_MSG = {
    invalid_token: '此連結無效',
    token_used: '此連結已使用過，帳號已建立',
    token_expired: '此連結已過期，請聯絡您的經銷商重新取得',
    not_provisioned: '設備尚未開通，請稍後再試',
};

function LicensePill({ l }) {
    const isCs = l.productType === 'CS';
    return (
        <span className={[
            'inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] font-medium font-mono border',
            isCs ? 'bg-accent-50 text-accent-700 border-accent-100' : 'bg-cream-100 text-ink-700 border-cream-300',
        ].join(' ')}>
            {l.productType === 'CS' ? '智慧客服' : '智慧總機'} · {l.seats} 席
        </span>
    );
}

export default function Sdx500SignupPage() {
    const [sp] = useSearchParams();
    const token = sp.get('token');
    const [info, setInfo] = useState(null);
    const [loadErr, setLoadErr] = useState(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ company: '', taxId: '', email: '', phone: '', password: '', confirm: '' });
    const [submitting, setSubmitting] = useState(false);
    const [submitErr, setSubmitErr] = useState(null);
    const [done, setDone] = useState(null);

    useEffect(() => {
        if (!token) { setLoadErr('缺少連結 token'); setLoading(false); return; }
        fetch(`/api/partner/sdx500/signup/${encodeURIComponent(token)}`)
            .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
            .then(({ ok, j }) => { if (ok) setInfo(j); else setLoadErr(ERR_MSG[j.code] || j.message || '連結無效'); })
            .catch((e) => setLoadErr(e.message))
            .finally(() => setLoading(false));
    }, [token]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    async function submit(e) {
        e.preventDefault();
        setSubmitErr(null);
        if (!form.company.trim()) return setSubmitErr('請填客戶公司名稱');
        if (!/^\d{8}$/.test(form.taxId.trim())) return setSubmitErr('統一編號須為 8 位數字');
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return setSubmitErr('Email 格式不正確');
        if (form.password.length < 6) return setSubmitErr('密碼至少 6 碼');
        if (form.password !== form.confirm) return setSubmitErr('兩次密碼不一致');
        setSubmitting(true);
        try {
            const r = await fetch('/api/partner/sdx500/signup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, email: form.email.trim(), company: form.company.trim(), taxId: form.taxId.trim(), phone: form.phone.trim(), password: form.password }),
            });
            const j = await r.json();
            if (!r.ok) throw new Error(j.message || j.error || '建立失敗');
            setDone(j);
        } catch (e) {
            setSubmitErr(e.message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="min-h-dvh bg-cream-50 flex flex-col items-center px-4 py-10">
            {/* brand */}
            <div className="flex items-center gap-2.5 text-[13px] text-ink-500 mb-8">
                <ShieldCheck size={16} className="text-accent-500" />
                <span><b className="text-ink-900 font-semibold">QubbyAI</b> <span className="text-cream-400">×</span> <b className="text-ink-900 font-semibold">TECOM</b> 帳號啟用</span>
            </div>

            <div className="w-full max-w-md">
                {loading ? (
                    <Card><div className="flex items-center justify-center gap-2 py-10 text-ink-400 text-sm"><Loader2 size={16} className="animate-spin" />載入中…</div></Card>
                ) : loadErr ? (
                    <Card>
                        <div className="text-center py-8">
                            <AlertCircle size={44} strokeWidth={1.3} className="text-danger-500/70 mx-auto mb-4" />
                            <h2 className="heading-display text-xl font-semibold text-ink-900 mb-2">無法啟用</h2>
                            <p className="text-sm text-ink-500">{loadErr}</p>
                        </div>
                    </Card>
                ) : done ? (
                    <Card>
                        <div className="text-center py-6">
                            <CheckCircle2 size={48} strokeWidth={1.3} className="text-success-500 mx-auto mb-4" />
                            <h2 className="heading-display text-2xl font-semibold text-ink-900 mb-2">帳號已建立</h2>
                            <p className="text-sm text-ink-500 mb-1">您的 Qubby 服務已啟用。</p>
                            <p className="text-[13px] text-ink-400 mb-6">
                                {done.ipPhoneRegistered ? '設備分機已自動上線。' : '設備分機註冊處理中，登入後可於後台確認 PBX 連線。'}
                            </p>
                            <a href={done.adminConsoleUrl || 'https://ivr-admin.qubby.io'}
                                className="inline-flex items-center gap-2 bg-accent-500 text-white text-sm font-medium px-5 py-2.5 rounded-md hover:bg-accent-700 transition">
                                前往管理後台登入 <ArrowRight size={15} />
                            </a>
                            <p className="text-[12px] text-ink-400 mt-4">用剛剛設定的 Email + 密碼登入</p>
                        </div>
                    </Card>
                ) : info?.status === 'already_active' ? (
                    <Card>
                        <div className="text-center py-6">
                            <CheckCircle2 size={48} strokeWidth={1.3} className="text-success-500 mx-auto mb-4" />
                            <h2 className="heading-display text-2xl font-semibold text-ink-900 mb-2">此授權已開通完成</h2>
                            <p className="text-sm text-ink-500 mb-1">這台設備的 Qubby 帳號先前已建立並啟用，無需重新開通。</p>
                            <div className="text-[13px] text-ink-400 mb-6 space-y-0.5">
                                {info.company && <p>公司：{info.company}</p>}
                                {info.ownerEmail && <p>管理者帳號：<span className="font-mono">{info.ownerEmail}</span></p>}
                                {info.macAddress && <p className="font-mono text-[11px] text-ink-300">設備 {info.macAddress}</p>}
                            </div>
                            <a href="https://ivr-admin.qubby.io"
                                className="inline-flex items-center gap-2 bg-accent-500 text-white text-sm font-medium px-5 py-2.5 rounded-md hover:bg-accent-700 transition">
                                前往管理後台登入 <ArrowRight size={15} />
                            </a>
                        </div>
                    </Card>
                ) : (
                    <Card>
                        {/* 經銷商 + 授權摘要 */}
                        <div className="mb-6 pb-5 border-b border-cream-200">
                            <div className="flex items-center gap-2 text-[12px] text-ink-400 font-mono uppercase tracking-wider mb-2">
                                <Building2 size={13} /> 服務提供經銷商
                            </div>
                            <div className="text-[15px] font-medium text-ink-900">{info?.distributor?.name || '—'}</div>
                            <div className="flex flex-wrap gap-2 mt-3">
                                {(info?.license || []).map((l, i) => <LicensePill key={i} l={l} />)}
                            </div>
                            {info?.macAddress && <div className="font-mono text-[11px] text-ink-300 mt-3">設備 {info.macAddress}</div>}
                        </div>

                        <h2 className="heading-display text-xl font-semibold text-ink-900 mb-1">建立客戶的管理者帳號</h2>
                        <p className="text-[13px] text-ink-500 mb-4">填寫後即啟用租戶，設備分機自動上線。</p>

                        {/* 經銷商代填提醒：欄位一律填「終端客戶」的資料，不是經銷商自己的 */}
                        <div className="mb-5 flex items-start gap-2 rounded-md bg-accent-50 border border-accent-100 px-3 py-2.5">
                            <AlertCircle size={15} className="text-accent-700 mt-0.5 shrink-0" />
                            <p className="text-[12.5px] text-accent-700 leading-relaxed">
                                此表單由經銷商代填。請務必填寫<b>終端客戶</b>的公司名稱、統一編號與聯絡電話（<b>非經銷商本身</b>）；帳號將建立給客戶登入使用。
                            </p>
                        </div>

                        <form onSubmit={submit} className="space-y-4">
                            <Input label="客戶公司名稱" value={form.company} onChange={set('company')} placeholder="終端客戶的公司抬頭" autoFocus />
                            <Input label="統一編號" value={form.taxId} onChange={set('taxId')} placeholder="客戶公司 8 碼統編" inputMode="numeric" maxLength={8} />
                            <Input label="客戶 Email（登入帳號）" type="email" value={form.email} onChange={set('email')} placeholder="客戶登入用信箱 you@company.com" />
                            <Input label="客戶聯絡電話" value={form.phone} onChange={set('phone')} placeholder="客戶電話 09xx-xxx-xxx" />
                            <Input label="密碼" type="password" value={form.password} onChange={set('password')} placeholder="至少 6 碼" />
                            <Input label="確認密碼" type="password" value={form.confirm} onChange={set('confirm')} placeholder="再輸入一次" />

                            {submitErr && <div className="text-[13px] text-danger-500">{submitErr}</div>}

                            <button type="submit" disabled={submitting}
                                className="w-full flex items-center justify-center gap-2 bg-accent-500 text-white text-sm font-medium py-2.5 rounded-md hover:bg-accent-700 disabled:opacity-50 transition">
                                {submitting ? <><Loader2 size={15} className="animate-spin" /> 建立中…</> : '建立帳號並啟用'}
                            </button>
                        </form>
                    </Card>
                )}

                <p className="text-center text-[11px] text-ink-300 mt-6 font-mono">QubbyAI × TECOM SDX500</p>
            </div>
        </div>
    );
}

function Card({ children }) {
    return <div className="bg-white border border-cream-200 rounded-xl shadow-[0_1px_0_rgba(28,26,23,0.02)] p-7">{children}</div>;
}

function Input({ label, type = 'text', ...rest }) {
    return (
        <div>
            <label className="block text-[12.5px] font-medium text-ink-700 mb-1.5">{label}</label>
            <input
                type={type}
                {...rest}
                className="w-full px-3 py-2 text-sm bg-white border border-cream-200 rounded-md focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20"
            />
        </div>
    );
}
